/**
 * Environment-variable layer.
 *
 * Every flag is overridable with `REACT_APP_FEATURE_<KEY>`, which must be
 * exactly `true` or `false`. An unset variable means "no override" (the
 * registry default or a chain override wins); any other value — including an
 * empty string — is a configuration error and throws.
 *
 * `readRawFeatureFlagEnv()` writes every `process.env.REACT_APP_*` access as a
 * LITERAL member expression. CRA's DefinePlugin only substitutes literal reads,
 * so dynamic `process.env[key]` access is deliberately avoided. Reading inside
 * a function (rather than caching at module scope) also keeps the values live
 * for tests, which mutate `process.env` directly.
 */
import { FeatureFlagConfigError } from "./errors";
import { FeatureFlagKey } from "./types";

/** `PERPETUAL_SPOT` -> `REACT_APP_FEATURE_PERPETUAL_SPOT`. */
export const featureFlagEnvKey = (key: FeatureFlagKey): string =>
  `REACT_APP_FEATURE_${key}`;

/**
 * Raw env values per flag. Typed as a full `Record`, so adding a flag without
 * adding its env line here is a compile error.
 */
const readRawFeatureFlagEnv = (): Record<FeatureFlagKey, string | undefined> => ({
  [FeatureFlagKey.PERPETUAL_FUTURES]:
    process.env.REACT_APP_FEATURE_PERPETUAL_FUTURES,
  [FeatureFlagKey.PERPETUAL_SPOT]: process.env.REACT_APP_FEATURE_PERPETUAL_SPOT,
  [FeatureFlagKey.OPTIONS]: process.env.REACT_APP_FEATURE_OPTIONS,
  [FeatureFlagKey.PREDICTION_MARKET]:
    process.env.REACT_APP_FEATURE_PREDICTION_MARKET,
  [FeatureFlagKey.NATIVE_CANDLE_CHART]:
    process.env.REACT_APP_FEATURE_NATIVE_CANDLE_CHART,
  [FeatureFlagKey.ADVANCED_CHART_UI]:
    process.env.REACT_APP_FEATURE_ADVANCED_CHART_UI,
  [FeatureFlagKey.PWA_UI]: process.env.REACT_APP_FEATURE_PWA_UI,
  [FeatureFlagKey.TRANSFER_UI]: process.env.REACT_APP_FEATURE_TRANSFER_UI,
  [FeatureFlagKey.CONVERT_UI]: process.env.REACT_APP_FEATURE_CONVERT_UI,
  [FeatureFlagKey.USER_SETTINGS_UI]:
    process.env.REACT_APP_FEATURE_USER_SETTINGS_UI,
  [FeatureFlagKey.ISOLATED_MARGIN_ADJUST]:
    process.env.REACT_APP_FEATURE_ISOLATED_MARGIN_ADJUST,
  [FeatureFlagKey.EDIT_ORDER]: process.env.REACT_APP_FEATURE_EDIT_ORDER,
  [FeatureFlagKey.API_KEYS_UI]: process.env.REACT_APP_FEATURE_API_KEYS_UI,
  [FeatureFlagKey.REFERRAL_REWARDS]:
    process.env.REACT_APP_FEATURE_REFERRAL_REWARDS,
  [FeatureFlagKey.SUBACCOUNTS_UI]: process.env.REACT_APP_FEATURE_SUBACCOUNTS_UI,
});

/**
 * Strictly parse one raw env value.
 * @returns `undefined` when the variable is unset (no override), else the boolean.
 * @throws {FeatureFlagConfigError} when the value is present but not `true`/`false`.
 */
export const parseFeatureFlagEnv = (
  key: FeatureFlagKey,
  raw: string | undefined
): boolean | undefined => {
  if (raw === undefined) {
    return undefined;
  }
  if (raw === "true") {
    return true;
  }
  if (raw === "false") {
    return false;
  }
  throw new FeatureFlagConfigError(
    `${featureFlagEnvKey(key)} must be exactly "true" or "false", got ${JSON.stringify(raw)}. ` +
      "Remove the variable to use the configured default."
  );
};

/** The env override for one flag, or `undefined` when unset. */
export const getFeatureFlagEnvOverride = (
  key: FeatureFlagKey
): boolean | undefined => parseFeatureFlagEnv(key, readRawFeatureFlagEnv()[key]);

/**
 * Parse every flag's env value. Used by `assertFeatureFlagsValid()` so a typo in
 * a flag nobody reads on the first render still fails at boot.
 */
export const validateFeatureFlagEnv = (): void => {
  const raw = readRawFeatureFlagEnv();
  (Object.keys(raw) as FeatureFlagKey[]).forEach((key) => {
    parseFeatureFlagEnv(key, raw[key]);
  });
};
